import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import ActionButtonOverlay from '../../components/navigation/ActionButtonOverlay';

import UserProfileHeader from '../../components/navigation/UserProfileHeader';
import FeelingLevelSelector from './components/FeelingLevelSelector';
import SymptomCategorySelector from './components/SymptomCategorySelector';
import BodyLocationMapper from './components/BodyLocationMapper';
import SeveritySlider from './components/SeveritySlider';
import TimingSelector from './components/TimingSelector';
import TriggerSelector from './components/TriggerSelector';
import DailyImpactAssessment from './components/DailyImpactAssessment';
import MedicationTracker from './components/MedicationTracker';
import NotesAndVoiceInput from './components/NotesAndVoiceInput';
import CompletionScreen from './components/CompletionScreen';
import ProfileSelector from './components/ProfileSelector';
import PhotoAttachment from './components/PhotoAttachment';
import Toast from '../../components/Toast';
import ReferralMilestoneCard from './components/ReferralMilestoneCard';

import alertService from '../../services/alertService';
import symptomService from '../../services/symptomService';
import { caregiverService } from '../../services/caregiverService';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Save, AlertTriangle, Gift } from 'lucide-react';
import subscriptionService from '../../services/subscriptionService';
import { Helmet } from 'react-helmet';
import CloseButton from '../../components/CloseButton';
import { trackDailyCheckIn } from '../../hooks/useGoogleAnalytics';
import { trackFirstCheckinComplete } from '../../hooks/useGoogleAnalytics';




const DailyCheckIn = () => {
  const navigate = useNavigate();
  const { user, resendVerificationEmail } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedEntry, setSavedEntry] = useState(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [isFirstEntry, setIsFirstEntry] = useState(false);
  const [checkInData, setCheckInData] = useState({
    feelingLevel: 5,
    symptoms: [],
    bodyLocations: [],
    severity: 5,
    timing: 'morning',
    triggers: [],
    medications: [],
    dailyImpact: {
      sleep: 5,
      energy: 5,
      mood: 5,
      appetite: 5
    },
    notes: '',
    voiceNote: null,
    photos: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, daysRemaining: 0 });
  const [upgradeRecommendation, setUpgradeRecommendation] = useState({ shouldUpgrade: false, recommendedPlan: null, reason: '' });
  const [isOffline, setIsOffline] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Auth session is managed by AuthContext - no need for redundant verification
  // Removed sessionReady, sessionLoading, sessionError states that were causing LockManager timeouts

  // Check email verification status
  useEffect(() => {
    const checkEmailVerification = async () => {
      if (user?.email_confirmed_at) {
        setEmailVerified(true);
      } else {
        setEmailVerified(false);
      }
    };

    checkEmailVerification();
  }, [user]);

  const checkProfileSelectorEligibility = async () => {
    try {
      const limits = await subscriptionService?.getPlanLimits();
      const maxManagedProfiles = limits?.managedProfiles || limits?.managed_profiles || 0;

      // CRITICAL FIX: Only show ProfileSelector if user actually has managed profiles
      // Don't show it just because their plan allows it - they need to have created profiles first
      if (maxManagedProfiles > 0) {
        // Check if user has any existing managed profiles
        const { data: managedProfiles, error } = await caregiverService?.getManagedProfiles();
        
        if (error) {
          console.error('Error checking managed profiles:', error);
          setShowProfileSelector(false);
          return;
        }
        
        // Only show selector if user has at least one managed profile
        const hasProfiles = managedProfiles && managedProfiles?.length > 0;
        setShowProfileSelector(hasProfiles);
        
        console.log('Profile selector eligibility:', {
          maxManagedProfiles,
          actualManagedProfiles: managedProfiles?.length || 0,
          showSelector: hasProfiles,
          reason: hasProfiles ? 'User has managed profiles' : 'No managed profiles exist yet'
        });
      } else {
        setShowProfileSelector(false);
        console.log('Profile selector eligibility:', {
          maxManagedProfiles: 0,
          showSelector: false,
          reason: 'Plan does not allow managed profiles'
        });
      }
    } catch (error) {
      console.error('Error checking profile selector eligibility:', error);
      setShowProfileSelector(false);
    }
  };

  useEffect(() => {
    checkEmailVerification();
    checkTrialStatus();
    checkProfileSelectorEligibility();
    checkIfFirstVisit();
    
    // Check for dependent parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const dependentId = urlParams?.get('dependent');
    if (dependentId) {
      setSelectedProfile(dependentId);
    }
    
    // Monitor connectivity status
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user]);

  const checkIfFirstVisit = async () => {
    if (!user) return;

    try {
      const { data, error } = await symptomService?.getEntries();
      // Show tour only if user has no entries and hasn't completed tour
      const tourCompleted = localStorage.getItem('dailyCheckInTour_completed');
      if (!error && (!data || data?.length === 0) && !tourCompleted) {
        setShowGuidedTour(true);
      }
      // Track if this is the user's first entry
      if (!error && (!data || data?.length === 0)) {
        setIsFirstEntry(true);
      }
    } catch (error) {
      console.error('Error checking first visit:', error);
    }
  };

  const checkEmailVerification = async () => {
    if (user?.email_confirmed_at) {
      setEmailVerified(true);
    } else {
      setEmailVerified(false);
    }
  };

  const checkTrialStatus = async () => {
    const trialInfo = await subscriptionService?.isUserInTrial();
    setTrialStatus(trialInfo);
    
    const recommendation = await subscriptionService?.getUpgradeRecommendation();
    setUpgradeRecommendation(recommendation);
  };

  const [formData, setFormData] = useState({
    feelingLevel: '',
    symptomCategories: [],
    otherSymptomText: '',
    bodyLocations: [],
    otherLocationText: '',
    severity: 3,
    timing: '',
    triggers: [],
    customTrigger: '',
    dailyImpacts: [],
    medicationStatus: '',
    medicationDetails: '',
    notes: '',
    timestamp: null
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const totalSteps = formData?.feelingLevel === 'good' ? 3 : 10;

  const handleFeelingLevelSelect = (level) => {
    setFormData(prev => ({ ...prev, feelingLevel: level }));
    if (level === 'good') {
      setCurrentStep(3);
    } else {
      setCurrentStep(2);
    }
  };

  const handleSymptomCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      symptomCategories: prev?.symptomCategories?.includes(categoryId)
        ? prev?.symptomCategories?.filter(id => id !== categoryId)
        : [...prev?.symptomCategories, categoryId]
    }));
  };

  const handleBodyLocationToggle = (locationId) => {
    setFormData(prev => ({
      ...prev,
      bodyLocations: prev?.bodyLocations?.includes(locationId)
        ? prev?.bodyLocations?.filter(id => id !== locationId)
        : [...prev?.bodyLocations, locationId]
    }));
  };

  const handleTriggerToggle = (triggerId) => {
    setFormData(prev => ({
      ...prev,
      triggers: prev?.triggers?.includes(triggerId)
        ? prev?.triggers?.filter(id => id !== triggerId)
        : [...prev?.triggers, triggerId]
    }));
  };

  const handleImpactToggle = (impactId) => {
    setFormData(prev => ({
      ...prev,
      dailyImpacts: prev?.dailyImpacts?.includes(impactId)
        ? prev?.dailyImpacts?.filter(id => id !== impactId)
        : [...prev?.dailyImpacts, impactId]
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // ===== CLIENT-SIDE VALIDATION BEFORE SUBMISSION =====
    // Check if required fields are filled
    if (!formData?.feelingLevel || formData?.feelingLevel === '') {
      setError('Please select how you\'re feeling today before saving your check-in.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // ===== END CLIENT-SIDE VALIDATION =====
    
    setIsSubmitting(true);
    setError(null); // Clear previous errors
    
    // ===== COMPREHENSIVE DIAGNOSTIC LOGGING START =====
    console.group('🔍 DAILY CHECK-IN SUBMISSION - FULL STATE CAPTURE');
    console.log('📊 Component State:', {
      selectedProfile: selectedProfile,
      selectedProfileType: typeof selectedProfile,
      selectedProfileId: selectedProfile?.id,
      selectedProfileIsObject: selectedProfile !== null && typeof selectedProfile === 'object',
      showProfileSelector: showProfileSelector,
      userEmail: user?.email,
      userId: user?.id
    });
    
    console.log('📋 Form Data:', {
      feelingLevel: formData?.feelingLevel,
      symptomCategories: formData?.symptomCategories,
      bodyLocations: formData?.bodyLocations,
      severity: formData?.severity,
      timing: formData?.timing
    });
    
    // CRITICAL FIX: Determine managed_profile_id with explicit null handling
    // Self users should ALWAYS have null, even if selectedProfile object exists
    let managedProfileId = null;
    
    if (selectedProfile) {
      // Check if selectedProfile is the 'self' profile
      if (selectedProfile?.id === 'self' || selectedProfile?.relationship === 'self') {
        managedProfileId = null;
        console.log('✅ Self profile detected - setting managedProfileId to NULL');
      } else if (selectedProfile?.id && selectedProfile?.id !== 'self') {
        // This is a real managed profile
        managedProfileId = selectedProfile?.id;
        console.log('👥 Managed profile detected:', {
          profileId: managedProfileId,
          profileName: selectedProfile?.name || selectedProfile?.first_name
        });
      } else {
        // Unexpected state - log warning and default to null
        console.warn('⚠️ Unexpected selectedProfile state:', selectedProfile);
        managedProfileId = null;
      }
    } else {
      console.log('ℹ️ No selectedProfile - defaulting to NULL (Self user)');
      managedProfileId = null;
    }
    
    console.log('🎯 Final managedProfileId Decision:', {
      managedProfileId: managedProfileId,
      isNull: managedProfileId === null,
      isSelfSubmission: managedProfileId === null,
      isCaregiverSubmission: managedProfileId !== null
    });
    // ===== COMPREHENSIVE DIAGNOSTIC LOGGING END =====
    
    const entry = {
      ...formData,
      timestamp: new Date()?.toISOString(),
      managedProfileId: managedProfileId // Use explicitly determined value
    };
    
    // ===== DIAGNOSTIC LOGGING: Final Entry =====
    console.log('📦 Entry Object Being Sent to Service:', {
      managedProfileId: entry?.managedProfileId,
      managedProfileIdType: typeof entry?.managedProfileId,
      managedProfileIdIsNull: entry?.managedProfileId === null,
      feelingLevel: entry?.feelingLevel,
      symptomCategories: entry?.symptomCategories,
      severity: entry?.severity,
      timestamp: entry?.timestamp,
      hasEncryptionPassword: !!entry?.encryptionPassword
    });
    console.groupEnd();
    // ===== END DIAGNOSTIC LOGGING =====
    
    try {
      // Save to database with automatic encryption
      const { data, error } = await symptomService?.createEntry(entry);
      
      if (error) {
        // ===== DIAGNOSTIC LOGGING: Service Error =====
        console.group('❌ Daily Check-In Save Failed');
        console.error('Service returned error:', {
          errorMessage: error?.message,
          errorAction: error?.action,
          errorCode: error?.code,
          errorDetails: error?.details,
          fullError: error,
          attemptedEntry: {
            managedProfileId: entry?.managedProfileId,
            feelingLevel: entry?.feelingLevel,
            symptomCategories: entry?.symptomCategories
          }
        });
        console.groupEnd();
        // ===== END DIAGNOSTIC LOGGING =====
        
        console.error('Failed to save symptom entry:', error);
        
        // ===== ENHANCED USER-FRIENDLY ERROR DISPLAY =====
        // Display specific error message from service
        const userMessage = error?.message || 'Failed to save your entry. Please try again.';
        const actionRequired = error?.action;
        
        // Handle different error actions
        if (actionRequired === 'REAUTHENTICATE') {
          // Session expired - redirect to sign in
          alert(userMessage + '\n\nYou will be redirected to the sign-in page.');
          setIsSubmitting(false);
          navigate('/sign-in');
          return;
        } else if (actionRequired === 'REFRESH_PROFILES') {
          // Profile access issue - suggest refresh
          const shouldRefresh = window.confirm(userMessage + '\n\nWould you like to refresh the page to reload profiles?');
          if (shouldRefresh) {
            window.location?.reload();
          } else {
            setError(userMessage);
          }
          setIsSubmitting(false);
          return;
        } else if (actionRequired === 'CHECK_FORM') {
          // Form validation issue - show error and let user fix
          setError(userMessage + ' Please review your entries.');
          setIsSubmitting(false);
          // Scroll to top to show error message
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } else if (actionRequired === 'CONTACT_SUPPORT') {
          // Permission issue - suggest support
          const shouldContact = window.confirm(userMessage + '\n\nWould you like to go to Settings to contact support?');
          if (shouldContact) {
            navigate('/settings-and-privacy');
          } else {
            setError(userMessage);
          }
          setIsSubmitting(false);
          return;
        } else if (actionRequired === 'WAIT') {
          // Duplicate entry - just show message
          setError(userMessage);
          setIsSubmitting(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } else {
          // Generic error or RETRY action - show message with retry option
          setError(userMessage);
          setIsSubmitting(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        // ===== END ENHANCED ERROR DISPLAY =====
      }
      
      // ===== DIAGNOSTIC LOGGING: Success =====
      console.group('✅ Daily Check-In Save Successful');
      console.log('Service returned data:', {
        entryId: data?.id,
        user_id: data?.user_id,
        managed_profile_id: data?.managed_profile_id,
        created_at: data?.created_at
      });
      console.groupEnd();
      // ===== END DIAGNOSTIC LOGGING =====
      
      console.log('Symptom entry saved:', data);
      setSavedEntry({ ...entry, id: data?.id });
      setShowSuccess(true);

      // Track daily check-in creation in GA4
      trackDailyCheckIn();

      // Check if this is the user's first check-in and fire first_checkin_complete
      try {
        const { data: allEntries } = await symptomService?.getEntries();
        // data includes the newly saved entry; if only 1 entry exists, it's the first
        if (allEntries && allEntries?.length === 1) {
          trackFirstCheckinComplete();
        }
      } catch (firstCheckErr) {
        console.error('Error checking first check-in status:', firstCheckErr);
      }

      // Show success toast notification
      setToastMessage('Check-in saved');
      setShowToast(true);
      
      // Process pending alerts after symptom entry
      alertService?.processPendingAlerts()?.catch(err => {
        console.error('Failed to process alerts:', err);
      });
      
      // Show success animation then completion screen
      setTimeout(() => {
        setShowSuccess(false);
        setShowCompletion(true);
      }, 2000);
    } catch (error) {
      // ===== DIAGNOSTIC LOGGING: Catch Block =====
      console.group('⚠️ Daily Check-In Unexpected Error');
      console.error('Caught exception:', {
        errorMessage: error?.message,
        errorStack: error?.stack,
        fullError: error,
        attemptedEntry: {
          managedProfileId: entry?.managedProfileId,
          feelingLevel: entry?.feelingLevel
        }
      });
      console.groupEnd();
      // ===== END DIAGNOSTIC LOGGING =====
      
      console.error('Error saving symptom entry:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    setResendMessage('');
    try {
      const { error } = await resendVerificationEmail(user?.email);
      if (error) {
        setResendMessage('Failed to send verification email. Please try again.');
      } else {
        setResendMessage('Verification email sent! Please check your inbox and spam folder.');
      }
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setResendMessage('An error occurred. Please try again later.');
    } finally {
      setResendingEmail(false);
    }
  };

  const isEmailUnverified = user && !user?.email_confirmed_at;

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData?.feelingLevel !== '';
      case 2:
        return formData?.symptomCategories?.length > 0;
      case 3:
        return formData?.feelingLevel === 'good' || formData?.bodyLocations?.length > 0;
      case 4:
        return true;
      case 5:
        return formData?.timing !== '';
      case 6:
        return formData?.triggers?.length > 0;
      case 7:
        return formData?.dailyImpacts?.length > 0;
      case 8:
        return formData?.medicationStatus !== '';
      case 9:
        return true;
      case 10:
        return true; // Final step - notes are optional
      default:
        return false;
    }
  };

  const tourSteps = [
    {
      targetId: 'feeling-level-selector',
      title: 'Start with How You Feel',
      description: 'Begin by selecting your overall feeling level today. If you\'re feeling good with no symptoms, you can quickly complete your check-in!',
      icon: 'Smile',
      position: 'bottom'
    },
    {
      targetId: 'symptom-category-selector',
      title: 'Select Your Symptoms',
      description: 'Choose all symptoms you\'re experiencing. You can select multiple categories that apply to you.',
      icon: 'Activity',
      position: 'bottom'
    },
    {
      targetId: 'severity-slider',
      title: 'Rate Symptom Severity',
      description: 'Use the slider to indicate how severe your symptoms are, from mild to severe.',
      icon: 'Sliders',
      position: 'bottom'
    },
    {
      targetId: 'notes-input',
      title: 'Add Notes (Optional)',
      description: 'You can add detailed notes about your symptoms or use voice input for convenience.',
      icon: 'FileText',
      position: 'top'
    },
    {
      targetId: 'save-button',
      title: 'Save Your Entry',
      description: 'When you\'re done, click Save to record your daily check-in. Your data is securely stored and you can view it in Symptom History.',
      icon: 'Save',
      position: 'top'
    }
  ];

  const renderStep = () => {
    if (showCompletion) {
      return <CompletionScreen entryData={savedEntry} entryId={savedEntry?.id} />;
    }

    if (formData?.feelingLevel === 'good' && currentStep === 3) {
      return (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 bg-success/10 rounded-full mb-6">
            <Icon name="Smile" size={48} color="var(--color-success)" strokeWidth={2} />
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Great to hear!
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-md mx-auto mb-8">
            We're glad you're feeling good today. Keep taking care of yourself!
          </p>
          <Button
            variant="default"
            size="lg"
            onClick={handleSubmit}
            disabled={isSubmitting}
            iconName="Check"
            iconPosition="right"
          >
            {isSubmitting ? 'Saving...' : 'Complete Check-in'}
          </Button>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <>
            {isFirstEntry && (
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Sparkles" size={20} className="text-teal-600" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-teal-900 mb-1">Welcome to Your First Check-In! 🎉</h3>
                    <p className="text-xs text-teal-700 mb-2">
                      This quick daily log helps you spot patterns, prepare for doctor visits, and take control of your health.
                    </p>
                    <ul className="space-y-1">
                      <li className="flex items-center gap-1.5 text-xs text-teal-700">
                        <Icon name="CheckCircle2" size={12} className="text-teal-500 flex-shrink-0" />
                        Takes just 2–3 minutes to complete
                      </li>
                      <li className="flex items-center gap-1.5 text-xs text-teal-700">
                        <Icon name="CheckCircle2" size={12} className="text-teal-500 flex-shrink-0" />
                        All data is securely encrypted and private
                      </li>
                      <li className="flex items-center gap-1.5 text-xs text-teal-700">
                        <Icon name="CheckCircle2" size={12} className="text-teal-500 flex-shrink-0" />
                        Build a health history you can share with your doctor
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <ReferralMilestoneCard />
            <FeelingLevelSelector
              selectedLevel={formData?.feelingLevel}
              onSelect={handleFeelingLevelSelect}
            />
          </>
        );
      case 2:
        return (
          <SymptomCategorySelector
            selectedCategories={formData?.symptomCategories}
            onToggle={handleSymptomCategoryToggle}
            otherText={formData?.otherSymptomText}
            onOtherTextChange={(text) => setFormData(prev => ({ ...prev, otherSymptomText: text }))}
          />
        );
      case 3:
        return (
          <BodyLocationMapper
            selectedLocations={formData?.bodyLocations}
            onToggle={handleBodyLocationToggle}
            otherLocationText={formData?.otherLocationText}
            onOtherLocationTextChange={(text) => setFormData(prev => ({ ...prev, otherLocationText: text }))}
          />
        );
      case 4:
        return (
          <SeveritySlider
            severity={formData?.severity}
            onChange={(value) => setFormData(prev => ({ ...prev, severity: value }))}
          />
        );
      case 5:
        return (
          <TimingSelector
            selectedTiming={formData?.timing}
            onSelect={(timing) => setFormData(prev => ({ ...prev, timing }))}
            timingContext={formData?.timingContext}
            onTimingContextChange={(context) => setFormData(prev => ({ ...prev, timingContext: context }))}
          />
        );
      case 6:
        return (
          <TriggerSelector
            selectedTriggers={formData?.triggers}
            onToggle={handleTriggerToggle}
            customTrigger={formData?.customTrigger}
            onCustomTriggerChange={(text) => setFormData(prev => ({ ...prev, customTrigger: text }))}
          />
        );
      case 7:
        return (
          <DailyImpactAssessment
            impacts={formData?.dailyImpacts}
            onToggle={handleImpactToggle}
          />
        );
      case 8:
        return (
          <MedicationTracker
            medicationStatus={formData?.medicationStatus}
            onSelect={(status) => setFormData(prev => ({ ...prev, medicationStatus: status }))}
            medicationDetails={formData?.medicationDetails}
            onDetailsChange={(details) => setFormData(prev => ({ ...prev, medicationDetails: details }))}
          />
        );
      case 9:
        return (
          <NotesAndVoiceInput
            notes={formData?.notes}
            onNotesChange={(notes) => setFormData(prev => ({ ...prev, notes }))}
          />
        );
      case 10:
        return (
          <PhotoAttachment
            photos={formData?.photos || []}
            onPhotosChange={(photos) => setFormData(prev => ({ ...prev, photos }))}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Daily Check-In - MyBodyToday</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-24">
        <UserProfileHeader />
        <BottomTabNavigation />
        <ActionButtonOverlay />
        
        {/* Header with Close Button */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <img
                  src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png"
                  alt="MyBodyToday Logo"
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain flex-shrink-0"
                />
                <div className="min-w-0">
                  <h1 className="text-sm sm:text-lg font-bold text-gray-900 leading-tight">MyBodyToday&#8482; — Daily Check-In</h1>
                  <p className="text-xs text-teal-600 font-medium italic leading-tight">Track small changes before they become big ones&#8482;</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CloseButton navigateTo="/dashboard" ariaLabel="Exit daily check-in" onClose={() => navigate('/dashboard')} />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 px-2.5 py-3 sm:px-3 sm:py-3.5 md:px-4 md:py-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-1">
                    Unable to Save Check-In
                  </h3>
                  <p className="text-sm text-red-700">
                    {error}
                  </p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-600 hover:text-red-800 flex-shrink-0"
                  aria-label="Dismiss error"
                >
                  <Icon name="X" className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          <div className="max-w-4xl mx-auto">
            {/* Profile Selector - Show on page 1 for Premium/Family plan users */}
            {currentStep === 1 && showProfileSelector && (
              <ProfileSelector
                selectedProfile={selectedProfile}
                onSelectProfile={setSelectedProfile}
              />
            )}

            {/* Referral Highlight Card — shown on step 1 */}
            {currentStep === 1 && (
              <div
                onClick={() => navigate('/referral-program')}
                className="cursor-pointer bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3 hover:border-green-400 transition-colors"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-green-900">💚 Earn Premium Free — Refer Friends!</p>
                  <p className="text-xs text-green-700 mt-0.5">5 referrals → Doctor Summary · 10 referrals → Full Premium</p>
                </div>
                <div className="text-green-600 flex-shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </div>
            )}

            {/* Step Indicator */}
            {!showCompletion && (
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Step {currentStep} of {totalSteps}
                </span>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalSteps })?.map((_, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i + 1 < currentStep
                          ? 'w-4 bg-primary'
                          : i + 1 === currentStep
                          ? 'w-6 bg-primary' :'w-4 bg-border'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {renderStep()}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-4 pt-6 border-t border-border">
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                disabled={currentStep === 1}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>

              {currentStep === totalSteps ? (
                <Button
                  id="save-button"
                  variant="default"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  iconName="Save"
                  iconPosition="left"
                  className="min-w-[140px]"
                >
                  {isSubmitting ? 'Saving...' : 'Save Entry'}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleNext}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </main>

        {showSuccess && (
          <div 
            className="fixed inset-0 bg-background/95 z-[300] flex items-center justify-center p-4"
            role="alert"
            aria-live="assertive"
          >
            <div className="bg-card rounded-xl shadow-therapeutic-xl p-8 text-center max-w-md">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mb-6">
                <Icon name="CheckCircle2" size={48} color="var(--color-success)" strokeWidth={2} />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-3">
                Check-in Complete!
              </h2>
              <p className="text-base md:text-lg text-muted-foreground">
                Your symptoms have been recorded successfully
              </p>
            </div>
          </div>
        )}
      </div>
      <BottomTabNavigation />
      <ActionButtonOverlay />
    </>
  );
};

export default DailyCheckIn;